import { Colorpicker, ColorpickerEvents } from "@dhx/ts-colorpicker";
import { locateNode } from "@dhx/ts-common/html";
import { Id } from "@dhx/ts-common/types";
import { NavigationBarEvents } from "@dhx/ts-navbar";
import { Popup } from "@dhx/ts-popup";
import { Toolbar } from "@dhx/ts-toolbar";
import { defaultDiffrentStyle, getToolbarData } from "../constants";
import { actionIdToModifierValue, startsWith, textColor, textSelectColor } from "../helpers/other";
import locale from "../locales/en";
import { Editor } from "../models/Editor";
import { Action, IRichText, Modifier, RichTextEvents, RTEToolbarBlock } from "../types";
import { statsRenderer } from "./statistics";
import { getComputedBlockStyle, getComputedNodeStyle } from "./styles";

// move to richtext config
const defaultStyle = {
	// textnode style
	[Modifier.fontFamily]: "Roboto",
	[Modifier.fontSize]: "14px",
	[Modifier.color]: "#4D4D4D",
	[Modifier.background]: "#FFFFFF",
	[Modifier.bold]: false,
	[Modifier.italic]: false,
	[Modifier.strike]: false,
	[Modifier.underline]: false,
	// block style
	[Modifier.style]: "p",
	[Modifier.blockquote]: false,
	[Modifier.align]: "left",
};

export class RichTextToolbarHelper {
	public toolbar: Toolbar;
	public colorpicker: Colorpicker;

	private _statsPopup: Popup;
	private _colorpickerPopup: Popup;

	private _colorpickerTarget: Modifier.background | Modifier.color;

	private _parent: IRichText;
	private _editor: Editor;

	private _defaultToolbarState;

	constructor(parent: IRichText, editor: Editor) {
		this._parent = parent;
		this._editor = editor;

		this.colorpicker = new Colorpicker(null);
		this._colorpickerPopup = new Popup();
		this._colorpickerPopup.attach(this.colorpicker);

		this._statsPopup = new Popup();
		this._parent.events.on(RichTextEvents.change, () => {
			if (this._statsPopup.isVisible()) {
				this._statsPopup.hide();
			}
		});

		const { toolbarBlocks } = parent.config;
		this.toolbar = new Toolbar(null, {
			css: "dhx_widget--bordered dhx_widget--bg_gray",
		});
		this.toolbar.data.parse(getToolbarData(toolbarBlocks, defaultStyle));

		this._defaultToolbarState = {};

		for (const block of toolbarBlocks) {
			switch (block) {
				case RTEToolbarBlock.align:
					this._defaultToolbarState[Modifier.align] = defaultStyle[Modifier.align];
					break;
				case RTEToolbarBlock.color:
					this._defaultToolbarState[Modifier.color] = defaultStyle[Modifier.color];
					this._defaultToolbarState[Modifier.background] = defaultStyle[Modifier.background];
					break;
				case RTEToolbarBlock.decoration:
					this._defaultToolbarState[Modifier.bold] = defaultStyle[Modifier.bold];
					this._defaultToolbarState[Modifier.italic] = defaultStyle[Modifier.italic];
					this._defaultToolbarState[Modifier.underline] = defaultStyle[Modifier.underline];
					this._defaultToolbarState[Modifier.strike] = defaultStyle[Modifier.strike];
					break;
				case RTEToolbarBlock.style:
					this._defaultToolbarState[Modifier.style] = defaultStyle[Modifier.style];
					this._defaultToolbarState[Modifier.fontFamily] = defaultStyle[Modifier.fontFamily];
					this._defaultToolbarState[Modifier.fontSize] = defaultStyle[Modifier.fontSize];
					this._defaultToolbarState[Modifier.blockquote] = defaultStyle[Modifier.blockquote];
					break;
			}
		}

		this._initEvents();
	}

	private _initEvents() {
		this._parent.events.on(RichTextEvents.selectionChange, () => {
			this._updateState();
		});
		this._parent.events.on(RichTextEvents.change, (action, canUndo, canRedo) => {
			if (this._parent.config.toolbarBlocks.includes(RTEToolbarBlock.undo)) {
				this._updateUndoRedoState(canUndo, canRedo);
			}
			if (action !== Action.update) {
				this._updateState();
			}
		});

		this.colorpicker.events.on(ColorpickerEvents.change, color => {
			this._colorpickerPopup.hide();
			this._parent.events.fire(RichTextEvents.action, [
				Action.update,
				{
					modifier: this._colorpickerTarget,
					modifierValue: color,
				},
				true,
			]);
			this.toolbar.data.update(this._colorpickerTarget, {
				html: this._colorpickerTarget === Modifier.color ? textColor(color) : textSelectColor(color),
			});
		});

		this.toolbar.events.on(NavigationBarEvents.click, (id, e) => this._toolbarAction(id, e));
	}

	private _toolbarAction(id: Id, e) {
		const item = this.toolbar.data.getItem(id);
		const active = !item.active;
		id = id.toString();

		switch (id) {
			case "bold":
				this._parent.events.fire(RichTextEvents.action, [
					Action.update,
					{
						modifier: Modifier.bold,
						modifierValue: active,
					},
					true,
				]);
				this.toolbar.data.update("bold", { active });
				break;
			case "underline":
				this._parent.events.fire(RichTextEvents.action, [
					Action.update,
					{
						modifier: Modifier.underline,
						modifierValue: active,
					},
					true,
				]);
				this.toolbar.data.update("underline", { active });
				break;
			case "italic":
				this._parent.events.fire(RichTextEvents.action, [
					Action.update,
					{
						modifier: Modifier.italic,
						modifierValue: active,
					},
					true,
				]);
				this.toolbar.data.update("italic", { active });
				break;
			case "strike":
				this._parent.events.fire(RichTextEvents.action, [
					Action.update,
					{
						modifier: Modifier.strike,
						modifierValue: active,
					},
					true,
				]);
				this.toolbar.data.update("strike", { active });
				break;
			case "color":
			case "background": {
				this._colorpickerTarget = id === Modifier.background ? Modifier.background : Modifier.color;
				const currentColor = this._getColorState(this._colorpickerTarget);
				this.colorpicker.setValue(currentColor);
				this.colorpicker.paint();
				this._colorpickerPopup.show(locateNode(e.target, "dhx_id") as HTMLElement, {
					mode: "bottom",
				});
				break;
			}
			case "link":
				e.stopPropagation();
				this._parent.events.fire(RichTextEvents.showLinkEditor);
				break;
			case "redo":
				this._parent.events.fire(RichTextEvents.action, [Action.redo, null, true]);
				break;
			case "undo":
				this._parent.events.fire(RichTextEvents.action, [Action.undo, null, true]);
				break;
			case "clear-style":
				this._parent.events.fire(RichTextEvents.action, [Action.clear, null, true]);
				break;
			case "blockquote":
				this._parent.events.fire(RichTextEvents.action, [
					Action.update,
					{
						modifier: Modifier.blockquote,
						modifierValue: active,
					},
					true,
				]);
				this.toolbar.data.update("blockquote", { active });
				break;
			case "fullscreen":
				if (item.$fullscreen) {
					this._parent.exitFullScreen();
				} else {
					this._parent.fullScreen();
				}
				break;
			case "stats": {
				const node = locateNode(e) as HTMLElement;
				const isRange = this._editor.selection.get().range;
				this._statsPopup.attach(
					statsRenderer(this._parent.getStats(isRange), this._parent.config.customStats)
				);
				this._statsPopup.show(node, { mode: "bottom", centering: true });
				break;
			}
			default: {
				const modifierValue = actionIdToModifierValue(id);
				switch (true) {
					case startsWith(id, "font-size"):
						if (modifierValue === "size") return;
						this._parent.events.fire(RichTextEvents.action, [
							Action.update,
							{
								modifier: Modifier.fontSize,
								modifierValue: modifierValue + "px",
							},
							true,
						]);
						this.toolbar.data.update(Modifier.fontSize, {
							value: modifierValue + "px",
						});
						break;
					case startsWith(id, "align"):
						this._parent.events.fire(RichTextEvents.action, [
							Action.update,
							{
								modifier: Modifier.align,
								modifierValue,
							},
							true,
						]);
						this.toolbar.data.update("align-left", {
							active: modifierValue === "left",
						});
						this.toolbar.data.update("align-right", {
							active: modifierValue === "right",
						});
						this.toolbar.data.update("align-center", {
							active: modifierValue === "center",
						});
						break;
					case startsWith(id, "style"):
						if (modifierValue === "style") return;
						this._parent.events.fire(RichTextEvents.action, [
							Action.update,
							{
								modifier: Modifier.style,
								modifierValue,
							},
							true,
						]);
						this.toolbar.data.update(Modifier.style, {
							value: locale[modifierValue],
						});
						break;
					case startsWith(id, "font"):
						if (modifierValue === "family") return;
						this._parent.events.fire(RichTextEvents.action, [
							Action.update,
							{
								modifier: Modifier.fontFamily,
								modifierValue: actionIdToModifierValue(id),
							},
							true,
						]);
						this.toolbar.data.update(Modifier.fontFamily, {
							value: modifierValue,
						});
						break;
				}
			}
		}
	}

	private _updateState() {
		const modifiers = this._getCommonModifiers();

		for (const key in modifiers) {
			if (this.toolbar.data.getItem(key)) {
				switch (key) {
					// block modifiers
					case Modifier.align:
						this.toolbar.data.update("align-left", {
							active: modifiers[key] === "left",
						});
						this.toolbar.data.update("align-right", {
							active: modifiers[key] === "right",
						});
						this.toolbar.data.update("align-center", {
							active: modifiers[key] === "center",
						});
						break;
					case Modifier.style:
						if (this._parent.config.toolbarBlocks.includes(RTEToolbarBlock.style)) {
							this.toolbar.data.update(key, { value: locale[modifiers[key]] });
						}
						break;
					case Modifier.blockquote:
						this.toolbar.data.update("blockquote", { active: modifiers[key] });
						break;
					// text modifiers
					case Modifier.color:
						this.toolbar.data.update(key, {
							html: textColor(modifiers[key]),
						});
						break;
					case Modifier.background:
						this.toolbar.data.update(key, {
							html: textSelectColor(modifiers[key]),
						});
						break;
					case Modifier.fontFamily:
					case Modifier.fontSize:
						this.toolbar.data.update(key, { value: modifiers[key] });
						break;
					case Modifier.italic:
					case Modifier.bold:
					case Modifier.underline:
					case Modifier.strike:
						this.toolbar.data.update(key, { active: modifiers[key] });
						break;
				}
			}
		}
	}
	private _updateUndoRedoState(canUndo: boolean, canRedo: boolean) {
		if (canUndo) {
			this.toolbar.enable("undo");
		} else {
			this.toolbar.disable("undo");
		}
		if (canRedo) {
			this.toolbar.enable("redo");
		} else {
			this.toolbar.disable("redo");
		}
	}
	private _getCommonModifiers() {
		let commonTextStyle;
		let commonBlockStyle;

		this._editor.eachSelectedNode((node, block) => {
			if (!commonTextStyle) {
				commonTextStyle = getComputedNodeStyle(node, block, this._defaultToolbarState);
				return;
			}
			const style = getComputedNodeStyle(node, block, this._defaultToolbarState);
			for (const key in commonTextStyle) {
				if (commonTextStyle[key] !== style[key]) {
					delete commonTextStyle[key];
				}
			}
		});
		this._editor.eachSelectedBlock(block => {
			if (!commonBlockStyle) {
				commonBlockStyle = getComputedBlockStyle(block, this._defaultToolbarState);
				return;
			}
			const style = getComputedBlockStyle(block, this._defaultToolbarState);
			for (const key in commonBlockStyle) {
				if (commonBlockStyle[key] !== style[key]) {
					delete commonBlockStyle[key];
				}
			}
		});
		return {
			...defaultDiffrentStyle,
			...commonBlockStyle,
			...commonTextStyle,
		};
	}
	private _getColorState(modifier) {
		const defaultColor = defaultStyle[modifier];
		let isSame = true;
		let initColor;

		this._editor.eachSelectedNode(node => {
			const color = node.style[modifier] || defaultColor;

			if (!initColor) {
				initColor = color;
			} else {
				isSame = isSame && initColor === color;
			}
		});

		return isSame ? initColor : defaultColor;
	}
}
